import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Flex, Text, Button, VStack, HStack, IconButton, Input, Select,
  NumberInput, NumberInputField, Tabs, TabList, Tab, TabPanels, TabPanel,
  Badge, Spinner, useDisclosure,
} from '@chakra-ui/react';
import { ArrowBackIcon, InfoIcon, DeleteIcon } from '@chakra-ui/icons';
import { useMICUTheme } from '../lib/micu/MICUThemeProvider';
import { db, logDeviceChange, deleteDevice } from '../lib/micu/micuDatabase';
import { getDeviceTemplate } from '../lib/micu/deviceTemplates';
import VentTidalVolumeInfo from '../components/micu/VentTidalVolumeInfo';

const DeviceField = ({ field, value, onChange, isDark }) => {
  if (field.type === 'select') {
    return (
      <Select
        variant="micu"
        size="sm"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">--</option>
        {field.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
      </Select>
    );
  }

  if (field.type === 'text') {
    return (
      <Input
        variant="micu"
        size="sm"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.label}
      />
    );
  }

  // Default: number
  return (
    <NumberInput
      size="sm"
      min={field.min}
      max={field.max}
      value={value || ''}
      onChange={(v) => onChange(v)}
    >
      <NumberInputField
        bg={isDark ? 'micu.bgSecondary' : 'micu.bg'}
        border="1px solid"
        borderColor="micu.border"
        color="micu.text"
        placeholder={field.label}
      />
    </NumberInput>
  );
};

const MICUDeviceDetail = () => {
  const { id, deviceId } = useParams();
  const caseId = Number(id);
  const devId = Number(deviceId);
  const navigate = useNavigate();
  const { isDark } = useMICUTheme();

  const [device, setDevice] = useState(null);
  const [template, setTemplate] = useState(null);
  const [settings, setSettings] = useState({});
  const [measurements, setMeasurements] = useState({});
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const ventInfoModal = useDisclosure();

  const loadData = useCallback(async () => {
    setLoading(true);
    const dev = await db.devices.get(devId);
    setDevice(dev);
    if (dev) {
      const tmpl = getDeviceTemplate(dev.deviceType);
      setTemplate(tmpl);

      // Load latest settings/measurements from logs
      const allLogs = await db.deviceLogs.where('deviceId').equals(devId).toArray();
      setLogs(allLogs.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)));

      const latestSettings = {};
      const latestMeasurements = {};
      for (const log of allLogs) {
        const data = typeof log.data === 'string' ? JSON.parse(log.data) : log.data;
        if (log.logType === 'settings') {
          Object.entries(data).forEach(([k, v]) => {
            if (!(k in latestSettings)) latestSettings[k] = v;
          });
        } else if (log.logType === 'measurements') {
          Object.entries(data).forEach(([k, v]) => {
            if (!(k in latestMeasurements)) latestMeasurements[k] = v;
          });
        }
      }
      setSettings(latestSettings);
      setMeasurements(latestMeasurements);
    }
    setLoading(false);
  }, [devId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSettingChange = async (key, value) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    await logDeviceChange(devId, 'settings', { [key]: value });
  };

  const handleMeasurementChange = async (key, value) => {
    const newMeasurements = { ...measurements, [key]: value };
    setMeasurements(newMeasurements);
    await logDeviceChange(devId, 'measurements', { [key]: value });
  };

  const handleDelete = async () => {
    await deleteDevice(devId);
    navigate(`/micu-mobile/case/${caseId}`);
  };

  if (loading) {
    return <Flex justify="center" align="center" minH="300px"><Spinner color="micu.teal" size="lg" /></Flex>;
  }

  if (!device || !template) {
    return <Box p={4}><Text color="micu.red">Device not found.</Text></Box>;
  }

  return (
    <Box p={4}>
      <Flex justify="space-between" align="center" mb={4}>
        <HStack spacing={2}>
          <IconButton
            icon={<ArrowBackIcon />}
            variant="micuGhost"
            size="sm"
            aria-label="Back"
            onClick={() => navigate(`/micu-mobile/case/${caseId}`)}
          />
          <Text fontSize="lg" fontWeight="bold" color="micu.text">{template.name}</Text>
        </HStack>
        <HStack spacing={2}>
          {device.deviceType === 'ventilator' && (
            <IconButton
              icon={<InfoIcon />}
              variant="micuGhost"
              size="sm"
              aria-label="Tidal volume info"
              onClick={ventInfoModal.onOpen}
            />
          )}
          <IconButton
            icon={<DeleteIcon />}
            variant="micuDanger"
            size="sm"
            aria-label="Delete device"
            onClick={handleDelete}
          />
        </HStack>
      </Flex>

      <Tabs variant="line" colorScheme="teal" size="sm">
        <TabList borderColor="micu.border">
          <Tab color="micu.textSecondary" _selected={{ color: 'micu.teal', borderColor: 'micu.teal' }}>Settings</Tab>
          <Tab color="micu.textSecondary" _selected={{ color: 'micu.teal', borderColor: 'micu.teal' }}>Measurements</Tab>
          <Tab color="micu.textSecondary" _selected={{ color: 'micu.teal', borderColor: 'micu.teal' }}>Log</Tab>
        </TabList>

        <TabPanels>
          <TabPanel px={0}>
            <VStack spacing={3} align="stretch">
              {template.inputs.map(field => (
                <Flex key={field.key} align="center" gap={3}>
                  <Text fontSize="sm" color="micu.textSecondary" minW="80px" flexShrink={0}>
                    {field.label}
                    {field.unit && <Text as="span" fontSize="2xs" color="micu.textMuted"> ({field.unit})</Text>}
                  </Text>
                  <Box flex={1}>
                    <DeviceField
                      field={field}
                      value={settings[field.key]}
                      onChange={(v) => handleSettingChange(field.key, v)}
                      isDark={isDark}
                    />
                  </Box>
                </Flex>
              ))}
            </VStack>
          </TabPanel>

          <TabPanel px={0}>
            <VStack spacing={3} align="stretch">
              {template.measurements.map(field => (
                <Flex key={field.key} align="center" gap={3}>
                  <Text fontSize="sm" color="micu.textSecondary" minW="80px" flexShrink={0}>
                    {field.label}
                    {field.unit && <Text as="span" fontSize="2xs" color="micu.textMuted"> ({field.unit})</Text>}
                  </Text>
                  <Box flex={1}>
                    <DeviceField
                      field={field}
                      value={measurements[field.key]}
                      onChange={(v) => handleMeasurementChange(field.key, v)}
                      isDark={isDark}
                    />
                  </Box>
                </Flex>
              ))}
            </VStack>
          </TabPanel>

          <TabPanel px={0}>
            <VStack spacing={2} align="stretch" maxH="400px" overflowY="auto">
              {logs.length === 0 ? (
                <Text fontSize="sm" color="micu.textMuted" textAlign="center" py={4}>No changes logged yet.</Text>
              ) : (
                logs.map(log => {
                  const data = typeof log.data === 'string' ? JSON.parse(log.data) : log.data;
                  const time = new Date(log.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
                  return (
                    <Box key={log.id} p={2} bg={isDark ? 'micu.bgTertiary' : 'micu.bgSecondary'} borderRadius="sm">
                      <HStack spacing={2}>
                        <Badge fontSize="2xs" colorScheme={log.logType === 'settings' ? 'teal' : 'blue'} variant="subtle">
                          {log.logType}
                        </Badge>
                        <Text fontSize="2xs" color="micu.textMuted">{time}</Text>
                      </HStack>
                      <Text fontSize="xs" color="micu.textSecondary" mt={1}>
                        {Object.entries(data).map(([k, v]) => `${k}: ${v}`).join(', ')}
                      </Text>
                    </Box>
                  );
                })
              )}
            </VStack>
          </TabPanel>
        </TabPanels>
      </Tabs>

      {device.deviceType === 'ventilator' && (
        <VentTidalVolumeInfo
          isOpen={ventInfoModal.isOpen}
          onClose={ventInfoModal.onClose}
          caseId={caseId}
        />
      )}
    </Box>
  );
};

export default MICUDeviceDetail;
